import type { AppError } from '@obraplan/core/application/errors/app-error';
import type { Session } from '@obraplan/core/application/session';
import type { Result } from '@obraplan/core/domain/shared/result';
import type { VerificationInput } from '@/features/planning/quality-verification-draft';
import type { PendingNonConformityPhotos } from '@/features/planning/quality-verification-draft';
import type { ShiftInput } from '@/features/planning/DailyReportWeather';
import type { CrewCount, EquipmentLine } from '@obraplan/core/domain/planning/daily-report-crew';
import type { DailyReportTask } from '@obraplan/core/domain/planning/daily-report-task';
import type { ReportTeamWorkInput, ReportWorkInput } from '@/features/planning/DailyReportWork';
import type { RemovedFrontResult } from '@obraplan/core/application/use-cases/planning/remove-inspection-allocation';

/**
 * A fila da Conferência no aparelho (Fase 43, etapa 2, RN-603 a RN-610, ADR-194).
 *
 * Cada registro é **uma operação da tela**, e não um endpoint solto: o "medir" são até seis
 * chamadas em sequência, e as do meio dependem do que o servidor respondeu antes — o id da
 * conferência, os números das não conformidades para as fotos. A fila guarda o que a pessoa
 * fez, e a operação refaz a sequência com as respostas de verdade.
 *
 * Toda operação leva um id desde a primeira tentativa, com sinal ou sem. Cada passo dela vai
 * com `<id>#<n>`, e o servidor não grava o mesmo passo duas vezes (RN-606): a primeira
 * tentativa que gravou e perdeu a resposta no caminho não vira gravação em dobro na segunda.
 */

/** A conferência ainda sem id no aparelho: o servidor a abre quando a operação subir. */
export const PENDING_INSPECTION_ID = 'aparelho:conferencia-pendente';

/**
 * A chave da ordem na fila do dia (RN-605). Para a pessoa, é o recurso dela; para a frente da
 * equipe e para o RDO, uma chave própria (`equipeNaFila`, `RDO_NA_FILA`).
 */
interface DoDia {
  readonly projectId: string;
  readonly date: string;
  readonly resourceId: string;
}

export type ConferenceOperation =
  | (DoDia & {
      readonly kind: 'medir';
      /** O id da conferência, quando a tela o tinha; sem ele, o primeiro passo a abre. */
      readonly inspectionId?: string;
      readonly measurements: readonly { allocationId: string; executedQuantity: number | null }[];
      readonly verifications: readonly VerificationInput[];
      readonly adjustments: readonly {
        allocationId: string;
        expectedVersion: number;
        quarterHours: number;
      }[];
      readonly justification?: string;
      readonly pieces: readonly {
        allocationId: string;
        activityId: string;
        serviceId: string;
        pieceNames: readonly string[];
        /** As do dia quando a lista abriu: a fila manda só a diferença (RN-615). */
        previousPieceNames?: readonly string[];
      }[];
      readonly photos: readonly PendingNonConformityPhotos[];
      readonly confirm: boolean;
    })
  | (DoDia & { readonly kind: 'ausencia'; readonly reason: string })
  | (DoDia & { readonly kind: 'reabrir'; readonly inspectionId: string })
  | (DoDia & {
      readonly kind: 'fotos-da-conferencia';
      readonly inspectionId?: string;
      readonly files: readonly File[];
      readonly caption: string;
    })
  | (DoDia & {
      readonly kind: 'fotos-da-nc';
      readonly nonConformityId: string;
      readonly files: readonly File[];
    })
  // Etapa 3: a medição por equipe (RN-611) e o RDO do dia (RN-612).
  | (DoDia & {
      readonly kind: 'medir-equipe';
      readonly teamId: string;
      readonly activityId: string;
      readonly serviceId: string;
      readonly quantity: number | null;
      readonly verifications: readonly VerificationInput[];
      readonly pieceNames?: readonly string[];
      /** As do dia quando a lista abriu: a fila manda só a diferença (RN-615). */
      readonly previousPieceNames?: readonly string[];
      readonly photos: readonly PendingNonConformityPhotos[];
    })
  | (DoDia & {
      readonly kind: 'fotos-da-equipe';
      /**
       * A medição, quando a tela tinha o id dela. Sem ele — medida sem sinal, ainda na fila —, a
       * foto vai atrás da medição, na chave da frente, e acha o id pela frente quando subir.
       */
      readonly teamMeasurementId?: string;
      /** A frente da equipe: é por ela que a foto acha a medição que ainda não tinha id. */
      readonly teamId?: string;
      readonly activityId?: string;
      readonly serviceId?: string;
      readonly files: readonly File[];
      readonly caption: string;
    })
  | (DoDia & { readonly kind: 'rdo-clima'; readonly shifts: readonly ShiftInput[] })
  | (DoDia & { readonly kind: 'rdo-tarefas'; readonly tasks: readonly DailyReportTask[] })
  | (DoDia & { readonly kind: 'rdo-ocorrencias'; readonly notes: string })
  | (DoDia & { readonly kind: 'rdo-efetivo'; readonly crew: readonly CrewCount[] })
  | (DoDia & { readonly kind: 'rdo-equipamento'; readonly equipment: readonly EquipmentLine[] })
  | (DoDia & {
      readonly kind: 'rdo-fotos';
      /** O documento do dia, quando a tela o tinha; sem ele, o primeiro passo o abre. */
      readonly reportId?: string;
      readonly files: readonly File[];
      readonly caption: string;
    })
  // Etapa 4: a execução por cômodo (RN-614).
  | (DoDia &
      DaFrente & {
        readonly kind: 'comodos';
        /** "Não se aplica" por cômodo da tipologia, antes do percentual. */
        readonly notApplicable: readonly { roomId: string; notApplicable: boolean }[];
        /** O percentual de cada cômodo de cada apto, no dia `date`. */
        readonly progress: readonly { positionId: string; roomId: string; percent: number }[];
      })
  | (DoDia &
      DaFrente & {
        readonly kind: 'pecas-da-frente';
        /**
         * O que muda em cada dia, na ordem: tirar de um dia e pôr noutro. A mudança, e não a lista
         * inteira: ao subir, a operação aplica-a à lista que o servidor tem (D22), sem desfazer o
         * que outra pessoa marcou enquanto o aparelho estava sem sinal.
         */
        readonly changes: readonly {
          date: string;
          add: readonly string[];
          remove: readonly string[];
        }[];
      })
  // Lançar o que ninguém planejou (RN-617): os cômodos, as peças, o trabalho com a checklist e
  // as fotos das NCs, na sequência do lançamento com sinal. A da pessoa vai na chave dela.
  | (DoDia & { readonly kind: 'lancar-trabalho'; readonly input: ReportWorkInput })
  | (DoDia & { readonly kind: 'lancar-trabalho-equipe'; readonly input: ReportTeamWorkInput })
  // Tirar a frente do dia da pessoa (RN-620, sobre a RN-631): a que não aconteceu sai das horas.
  | (DoDia & {
      readonly kind: 'tirar-frente';
      readonly allocationId: string;
      readonly expectedVersion: number;
    })
  // As ações que pediam sinal (RN-621 a RN-625). Remover a foto que já subiu (RN-621): a que
  // ainda espera na fila sai dela, sem virar operação (`Outbox.dropFile`, RN-622).
  | (DoDia & { readonly kind: 'remover-foto'; readonly attachmentId: string })
  // Encerrar a não conformidade com a solução (RN-623). A chave é a da NC (`ncNaFila`): as fotos
  // da solução, na mesma chave, sobem na ordem em que foram feitas.
  | (DoDia & {
      readonly kind: 'resolver-nc';
      readonly nonConformityId: string;
      readonly solution: string;
      readonly expectedVersion: number;
    })
  // Emitir e revisar o RDO (RN-624, RN-625), na chave do RDO: depois do que foi gravado nele.
  | (DoDia & { readonly kind: 'rdo-emitir' })
  | (DoDia & { readonly kind: 'rdo-revisar' });

/** A chave da frente na ordem da fila: os cômodos e as peças de um serviço numa frente. */
export function frenteNaFila(activityId: string, serviceId: string): string {
  return `frente:${activityId}:${serviceId}`;
}

/** O que a operação de cômodos e de peças da frente leva (etapa 4). */
interface DaFrente {
  readonly activityId: string;
  readonly serviceId: string;
}

/** A chave do RDO na ordem da fila: um documento por obra e dia, e tudo nele em ordem. */
export const RDO_NA_FILA = 'rdo';

/** A chave da não conformidade na ordem da fila: a solução e as fotos dela (RN-623). */
export function ncNaFila(nonConformityId: string): string {
  return `nc:${nonConformityId}`;
}

/** A chave da frente da equipe na ordem da fila. */
export function equipeNaFila(teamId: string, activityId: string, serviceId: string): string {
  return `equipe:${teamId}:${activityId}:${serviceId}`;
}

/** O que a operação respondeu quando subiu na hora. */
export interface OperationOutcome {
  readonly measured: number;
  readonly verified: number;
  readonly nonConformities: number;
  readonly adjusted: number;
  readonly confirmed: boolean;
  readonly pieces: number;
  readonly photos: { readonly sent: number; readonly failures: readonly string[] };
  /** A medição da equipe: o id dela, ou `null` quando a quantidade foi tirada. */
  readonly teamMeasurementId?: string | null;
  /** As peças da frente: quantas entraram e quantas saíram das listas dos dias. */
  readonly pieceMarks?: { readonly marked: number; readonly unmarked: number };
  /** A frente tirada do dia, com o que saiu junto (RN-631 a RN-634). */
  readonly removedFront?: RemovedFrontResult;
  /** O RDO emitido: o número que o servidor deu e a revisão (RN-624). */
  readonly report?: { readonly number: number; readonly revision: number };
}

/** `queued`: sem sinal, a operação ficou no aparelho e sobe sozinha quando ele voltar. */
export type SubmitOutcome =
  { readonly queued: false; readonly outcome: OperationOutcome } | { readonly queued: true };

export type OutboxState = 'pending' | 'rejected';

export interface OutboxEntry {
  readonly id: string;
  readonly userId: string;
  /** A hora do aparelho em que a pessoa fez — vai para o histórico (RN-607). */
  readonly createdAt: string;
  readonly operation: ConferenceOperation;
  readonly state: OutboxState;
  /** O motivo do servidor, quando ele recusou (RN-608). */
  readonly error?: { readonly code: string; readonly message: string };
  readonly attempts: number;
}

export interface Outbox {
  /**
   * Grava agora, ou guarda para depois (RN-603).
   *
   * Recusa do servidor volta como recusa — a pessoa está olhando para a tela, e corrige na hora.
   * Só a falta de resposta guarda a operação no aparelho.
   */
  submit(
    operation: ConferenceOperation,
    session: Session,
  ): Promise<Result<SubmitOutcome, AppError>>;
  /** O que espera na fila, de quem está com a sessão, na ordem em que foi feito. */
  list(userId: string, filter?: { projectId?: string; date?: string }): Promise<OutboxEntry[]>;
  /** Sobe o que estiver na fila desta sessão (RN-605). Devolve quantos subiram. */
  flush(session: Session): Promise<number>;
  /** O recusado volta para a fila, como estava, e a fila sobe em seguida (RN-608). */
  retry(id: string, session: Session): Promise<void>;
  /** O recusado sai da fila, e o que ele faria não acontece (RN-608). */
  discard(id: string): Promise<void>;
  /**
   * Tira da fila um arquivo que ainda não subiu (RN-622): a operação de fotos fica com os outros,
   * e sem nenhum sai da fila. A que já foi tentada pode ter subido em parte — os passos contam
   * pela posição do arquivo (RN-606) — e é recusada: a foto se remove depois de subir.
   */
  dropFile(id: string, index: number): Promise<Result<void, AppError>>;
}
